import React, { useEffect, useState } from 'react';
import { formatMoney } from "../../../../common/money";
import "./DecisionLateCheckout.css";
import Header from '../../../components/Header';
import remark from "../../../../assets/icons/ChatText.svg"
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from "../../../../customHooks/useAuth";
import { toast } from 'react-toastify';
import PageLoader from '../../../components/PageLoader/PageLoader';
import axiosInstance from '../../../../axiosInstance/axiosInstance';
import { HOTEL_TIMEZONE } from "../../../../common/hotelTime";

export default function DecisionLateCheckout() {

  const location = useLocation();
  const navigate = useNavigate();

  const userId = location.state?.userId || "";
  const bookingId = location.state?.bookingId || "";
  const bookedRoomId = location.state?.bookedRoomId || "";
  const hotelId = location.state?.hotelId || "";
  const requestId = location.state?.requestId || "";
  const data = location.state?.data || {};
  const roomId = location.state?.roomId || "";
  const status = location.state?.status || "";

  // Refresh Handle
  useAuth();

  const isEditable = status === "Pending_Hotel";

  const [isEditing, setIsEditing] = useState(false);
  const [hours, setHours] = useState(data.hours_late || 1);
  const [maxHours, setMaxHours] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(data.hourly_rate || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check Id
  useEffect(() => {
    const checkId = ()=>{
      if(userId==="" || bookingId==="" || requestId===""){
        navigate("/bookings");
      }
    }
    checkId();
  },[userId,bookingId,requestId,navigate]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/late-checkout-info`);
        setMaxHours(response?.data?.late_checkout_max_hours ?? null);
        setHourlyRate(response?.data?.late_checkout_hourly_rate ?? null);
      } catch (error) {
        console.log(error);
      }
    };
    if (isEditable && hotelId !== "") {
      fetchConfig();
    }
  }, [isEditable, hotelId]);


  // Function to format the date (e.g., "07 Oct")
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  };

  // Function to extract the year (e.g., "2024")
  const formatYear = (dateString) => {
    const date = new Date(dateString);
    return date.getFullYear();
  };

  // Function to format the time (e.g., "22:11")
  const formatTimePeriod = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
            timeZone: HOTEL_TIMEZONE, hour: '2-digit', minute: '2-digit' });
  };


  // Cancel Request
  const closeRequest = async()=>{
    try{
      await axiosInstance.put(`/api/v1/users/${userId}/guest-requests/${requestId}/close`);
      toast.success("Late Checkout Request Cancelled Successfully");
      navigate("/bookings",{state : {activeRequests : "requests"}});
    }catch(error){
      toast.error(error?.response?.data?.message || "Some Error Occured");
      console.log(error);
    }
  }

  const decreaseHours = () => setHours((current) => Math.max(1, current - 1));
  const increaseHours = () => setHours((current) => Math.min(maxHours || current, current + 1));

  const startEditing = () => {
    setHours(data.hours_late || 1);
    setIsEditing(true);
  };

  // Update Request
  const updateRequest = async()=>{
    if (hours === data.hours_late) {
      setIsEditing(false);
      return;
    }

    setIsSubmitting(true);
    try{
      await axiosInstance.put(
        `/api/v1/users/${userId}/bookings/${bookingId}/booked-rooms/${bookedRoomId}/guest-requests/post-checkin/late-checkout-request/${requestId}`,
        { hours_late : hours }
      );
      toast.success("Late Checkout Request Updated Successfully");
      navigate("/bookings",{state : {activeRequests : "requests"}});
    }catch(error){
      toast.error(error?.response?.data?.message || "Some Error Occured");
      console.log(error);
    }finally{
      setIsSubmitting(false);
    }
  }

  return (
    <div className="lateCheckout">
    {isSubmitting && <PageLoader/>}
    <Header title="Late Checkout" para="The hotel will confirm your late checkout request"/>

    <div className="lateCheckout-timing">

      <div className="lateCheckout-data">

        <div className='roomId'>
          <div className='roomId-label'>
              <h6>Room</h6>
          </div>
          <div className='roomId-data'>
            <h5>Room {roomId}</h5>
          </div>
        </div>

        <div className='time'>
          <div className='time-label'>
            <h6>Requested Checkout time</h6>
          </div>
          <div className='checkOut-time'>
            <h5>{formatDate(data.proposed_checkout_time)} {formatYear(data.proposed_checkout_time)}  {formatTimePeriod(data.proposed_checkout_time)}</h5>
          </div>
        </div>
      </div>

      {isEditing ? (
        <div className='lateCheckout-edit'>
          <h6>How much longer do you need the room?</h6>
          <div className='lateCheckout-edit-hours'>
            <div className='qtyButton' onClick={decreaseHours}>-</div>
            <div className='qty'>{hours} {hours === 1 ? "hour" : "hours"}</div>
            <div className='qtyButton' onClick={increaseHours}>+</div>
          </div>
          {maxHours !== null && <span className='lateCheckout-edit-hint'>Up to {maxHours} hours late</span>}
          {hourlyRate !== null && <span className='lateCheckout-edit-hint'>New fee: ₹{hours * hourlyRate}</span>}
        </div>
      ) : (
        <>
          {data.hours_late > 0 &&
            <div className='lateCheckout-Fees'>
              <div className='fee-label'>
                <h6>Hours late</h6>
              </div>
              <div className='fee-input'>
                <h5>{data.hours_late} {data.hours_late === 1 ? "hour" : "hours"}{data.hourly_rate ? ` × ₹${data.hourly_rate}` : ""}</h5>
              </div>
            </div>
          }

          {data.price!==null && data.price!==undefined &&
            <>
              <div className='lateCheckout-Fees'>
                <div className='fee-label'>
                  <h6>Fee</h6>
                </div>
                <div className='fee-input'>
                  <h5>{formatMoney(data.price || 0)} ₹</h5>
                </div>
              </div>

              {data.discount_amount > 0 && (
                <div className='lateCheckout-Fees'>
                  <div className='fee-label'>
                    <h6>Discount</h6>
                  </div>
                  <div className='fee-input'>
                    <h5>- {formatMoney(data.discount_amount)} ₹</h5>
                  </div>
                </div>
              )}

              {data.tax_amount > 0 && (
                <div className='lateCheckout-Fees'>
                  <div className='fee-label'>
                    <h6>GST</h6>
                  </div>
                  <div className='fee-input'>
                    <h5>+ {formatMoney(data.tax_amount)} ₹</h5>
                  </div>
                </div>
              )}

              <div className='lateCheckout-Fees'>
                <div className='fee-label'>
                  <h6>Total</h6>
                </div>
                <div className='fee-input'>
                  <h5>
                    {formatMoney(
                      (data.price || 0)
                        - (data.discount_amount || 0)
                        + (data.tax_amount || 0)
                    )} ₹
                  </h5>
                </div>
              </div>
            </>
          }
        </>
      )}

    </div>

    {data.remarks &&
      <div className='lateCheckout-remarks'>
        <div className='remarks'>
          <div className='remarks-img'>
            <img src={remark} alt="Remark" />
          </div>
          <div className='remarks-header'>
            <h6>Remarks By Hotel :</h6>
            <p>{data.remarks}</p>
          </div>
        </div>
      </div>
    }



    <div className='lateCheckout-desc'>
      <p>
      Your room will be held past the standard checkout time once the hotel confirms this request.
      </p>
    </div>


    {isEditing &&
      <div className="lateCheckout-btn">
        <div className="late-accept-btn">
          <button onClick={updateRequest} disabled={isSubmitting}>Save Changes</button>
        </div>
        <div className="late-cancel-btn">
          <button onClick={()=>setIsEditing(false)}>Discard</button>
        </div>
      </div>
    }

    {!isEditing &&
      (status==="Pending_Hotel" || status==="InProgress_Hotel" || status==="Scheduled_Hotel") &&
      <div className="lateCheckout-btn">
        {isEditable &&
          <div className="late-accept-btn">
            <button onClick={startEditing}>Edit Request</button>
          </div>
        }
        <div className="late-cancel-btn">
          <button onClick={closeRequest}>Cancel Request</button>
        </div>
      </div>
    }

    </div>
  )
}
